import type { ColumnDef } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import type { PromoOffer } from '../../types';
import { PromoOfferActions } from '../PromoOfferActions/PromoOfferActions';

interface GetColumnsOptions {
  onEdit?: (promoOffer: PromoOffer) => void;
  onViewDetails?: (promoOffer: PromoOffer) => void;
}

export const getPromoOfferColumns = (options: GetColumnsOptions = {}): ColumnDef<PromoOffer>[] => [
  {
    accessorKey: 'id',
    header: 'ID',
    cell: ({ row }) => (
      <div data-testid={`promo-offer-id-${row.original.id}`} className="font-mono text-xs">
        {row.original.id}
      </div>
    ),
    size: 60,
  },
  {
    accessorKey: 'promo_code',
    header: 'Promo Code',
    cell: ({ row }) => (
      <div data-testid={`promo-offer-code-${row.original.id}`}>
        {options.onViewDetails ? (
          <button
            className="font-mono font-bold text-primary hover:underline text-left"
            onClick={() => options.onViewDetails?.(row.original)}
          >
            {row.original.promo_code}
          </button>
        ) : (
          <span className="font-mono font-bold">
            {row.original.promo_code}
          </span>
        )}
      </div>
    ),
  },
  {
    accessorKey: 'venue_name',
    header: 'Venue',
    cell: ({ row }) => (
      <div data-testid={`promo-offer-venue-${row.original.id}`}>
        <p className="font-medium">{row.original.venue_name}</p>
        <p className="text-xs text-muted-foreground">{row.original.organization_name}</p>
      </div>
    ),
  },
  {
    accessorKey: 'brand',
    header: 'Brand',
    cell: ({ row }) => (
      <div data-testid={`promo-offer-brand-${row.original.id}`}>
        {row.original.brand || <span className="text-muted-foreground">N/A</span>}
      </div>
    ),
  },
  {
    accessorKey: 'short_description',
    header: 'Description',
    cell: ({ row }) => (
      <div data-testid={`promo-offer-description-${row.original.id}`} className="max-w-md">
        <p className="truncate">{row.original.short_description}</p>
      </div>
    ),
  },
  {
    accessorKey: 'max_claim_per_person',
    header: 'Per Person',
    cell: ({ row }) => (
      <div data-testid={`promo-offer-max-claim-per-person-${row.original.id}`} className="text-center">
        {row.original.max_claim_per_person}
      </div>
    ),
    size: 100,
  },
  {
    accessorKey: 'claimed_count',
    header: () => <div className="text-center">Claimed</div>,
    cell: ({ row }) => (
      <div data-testid={`promo-offer-claimed-${row.original.id}`} className="text-center">
        {row.original.claimed_count}
      </div>
    ),
    size: 80,
  },
  {
    accessorKey: 'redeemed_claims',
    header: () => <div className="text-center">Redeemed</div>,
    cell: ({ row }) => (
      <div data-testid={`promo-offer-redeemed-${row.original.id}`} className="text-center">
        {row.original.redeemed_claims}
      </div>
    ),
    size: 80,
  },
  {
    id: 'used',
    header: () => <div className="text-center">Used/Total</div>,
    cell: ({ row }) => {
      const claimedCount = row.original.claimed_count;
      const redeemedCount = row.original.redeemed_claims;
      const maxClaims = row.original.max_claim_total;
      const usedCount = claimedCount + redeemedCount;
      const percentage = Math.round((usedCount / maxClaims) * 100);
      const isFull = usedCount >= maxClaims;
      
      return (
        <div data-testid={`promo-offer-used-${row.original.id}`} className="text-center">
          <div className={isFull ? 'text-destructive font-medium' : ''}>
            {usedCount} / {maxClaims}
          </div>
          <div className="text-xs text-muted-foreground">
            ({percentage}%)
          </div>
        </div>
      );
    },
    size: 120,
  },
  {
    accessorKey: 'expires_at',
    header: 'Expires',
    cell: ({ row }) => {
      const expiryDate = new Date(row.original.expires_at);
      const isExpired = expiryDate < new Date();
      const formattedDate = expiryDate.toLocaleDateString();
      
      return (
        <div data-testid={`promo-offer-expiry-${row.original.id}`}>
          <span className={isExpired ? 'text-destructive' : ''}>
            {formattedDate}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: 'is_active',
    header: 'Status',
    cell: ({ row }) => {
      const isActive = row.original.is_active;
      const isExpired = new Date(row.original.expires_at) < new Date();
      const usedCount = row.original.claimed_count + row.original.redeemed_claims;
      const isFull = usedCount >= row.original.max_claim_total;
      
      let status = 'Inactive';
      let variant: 'default' | 'secondary' | 'destructive' = 'secondary';
      let className = '';
      
      if (isExpired) {
        status = 'Expired';
        variant = 'destructive';
      } else if (isFull) {
        status = 'Full';
        variant = 'secondary';
      } else if (isActive) {
        status = 'Active';
        variant = 'default';
        className = 'bg-green-100 text-green-800';
      }
      
      return (
        <Badge
          variant={variant}
          data-testid={`promo-offer-status-${row.original.id}`}
          className={className}
        >
          {status}
        </Badge>
      );
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <PromoOfferActions 
        promoOffer={row.original} 
        onEdit={options.onEdit}
      />
    ),
    size: 50,
    meta: {
      align: 'right',
    },
  },
];